import {
    Bet,
    calcResultNumber,
    calcUserProfit,
    createHashChain,
    createTypedData,
    fromGweiToWei,
    GameStatus as ContractStatus,
    keccak,
    ReasonEnded as ContractReasonEnded,
    verifySeed,
    verifySignature,
} from "@dicether/state-channel";
import * as Sentry from "@sentry/browser";
import {
    getConnection,
    getPublicClient,
    getTransactionReceipt,
    readContract,
    signTypedData,
    simulateContract,
    waitForTransactionReceipt,
    writeContract,
} from "@wagmi/core";
import retry from "async-retry";
import axios from "axios";
import {parseEventLogs} from "viem";

import {
    addBet,
    creatingGame,
    endedGame,
    endedWithReason,
    gameCreated,
    restoreState,
    revealSeed,
    serverConflictEnd,
    userAbortConflictEnd,
    userAbortForceEnd,
    userConflictEnd,
    userInitiateConflictEnd,
    userInitiateForceEnd,
} from "./actions";
import {ReasonEnded, State, State as GameState} from "./reducer";
import {CHAIN_ID, CONTRACT_ADDRESS, NETWORK_NAME, SERVER_ADDRESS, SIGNATURE_VERSION} from "../../../../config/config";
import wagmiConfig from "../../../../config/wagmiConfig";
import {getLogGameCreated, getReasonEnded} from "../../../../contractUtils";
import {abi} from "../../../../GameChannelContract";
import {Dispatch, GetState, isLocalStorageAvailable} from "../../../../util/util";
import {addNewBet} from "../../bets/asyncActions";
import {Bet as FinalBet} from "../../bets/types";
import {catchError} from "../../utilities/asyncActions";

const STORAGE_VERSION = 2;

//
// Event handling
//
function canCreateGame(gameState: GameState) {
    const status = gameState.status;
    return status === "ENDED" || (status === "CREATING" && !gameState.createTransactionHash);
}

function createGameEvent(hashChain: string[], serverEndHash: string, value: number, transactionHash?: string) {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canCreateGame(getState().games.gameState)) {
            dispatch(creatingGame(hashChain, serverEndHash, value, transactionHash));
        } else {
            Sentry.captureMessage("Unexpected createGameEvent");
        }
    };
}

function canEndGame(gameState: GameState) {
    const status = gameState.status;
    return status !== "ENDED";
}

function endGameEvent(reason: ReasonEnded) {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canEndGame(getState().games.gameState)) {
            dispatch(endedWithReason(reason));
        } else {
            Sentry.captureMessage("Unexpected endGameEvent");
        }
    };
}

function canRegularEndGame(gameState: GameState) {
    const status = gameState.status;
    return status === "ACTIVE";
}

export function canUserEndGame(gameState: GameState) {
    const status = gameState.status;
    const reasonEnded = gameState.reasonEnded;

    return status == "ENDED" && reasonEnded == "REGULAR_ENDED";
}

function regularEndGameEvent(
    roundId: number,
    serverHash: string,
    userHash: string,
    serverSig: string,
    userSig: string,
    endTransactionHash: string,
) {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canRegularEndGame(getState().games.gameState)) {
            dispatch(endedGame(roundId, serverHash, userHash, serverSig, userSig, endTransactionHash));
        } else {
            Sentry.captureMessage("Unexpected regularEndGameEvent");
        }
    };
}

function canActivateGame(gameState: GameState) {
    const status = gameState.status;
    return status === "CREATING";
}

function activateGameEvent(gameId: number, serverHash: string, userHash: string) {
    return (dispatch: Dispatch, getState: GetState) => {
        const gameState = getState().games.gameState;
        if (canActivateGame(gameState)) {
            if (serverHash !== gameState.serverHash) {
                throw Error(`Unexpected serverHash: ${serverHash}, expected ${gameState.serverHash}`);
            }
            if (userHash !== gameState.userHash) {
                throw Error(`Unexpected userHash: ${userHash}, expected ${gameState.userHash}`);
            }

            dispatch(gameCreated(gameId));
        } else {
            Sentry.captureMessage("Unexpected activateGameEvent");
        }
    };
}

function canPlaceBet(gameState: GameState) {
    const status = gameState.status;
    return status === "ACTIVE";
}

function placeBetEvent(bet: Bet, serverSig: string, userSig: string) {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canPlaceBet(getState().games.gameState)) {
            dispatch(addBet(bet, serverSig, userSig));
        } else {
            Sentry.captureMessage("Unexpected placeBetEvent");
        }
    };
}

function canRevealSeed(gameState: GameState) {
    const status = gameState.status;
    return status === "PLACED_BET";
}

function revealSeedEvent(serverSeed: string, userSeed: string, balance: number) {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canRevealSeed(getState().games.gameState)) {
            dispatch(revealSeed(serverSeed, userSeed, balance));
        } else {
            Sentry.captureMessage("Unexpected revealSeedEvent");
        }
    };
}

export function canUserInitiateConflictEnd(gameState: GameState) {
    const status = gameState.status;
    return status === "ACTIVE" || status === "PLACED_BET" || status === "SERVER_CONFLICT_ENDED";
}

function userInitiateConflictEndEvent(transactionHash: string) {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canUserInitiateConflictEnd(getState().games.gameState)) {
            dispatch(userInitiateConflictEnd(transactionHash));
        } else {
            Sentry.captureMessage("Unexpected userInitiateConflictEndEvent");
        }
    };
}

function canUserConflictEnd(gameState: GameState) {
    const status = gameState.status;
    return status === "USER_INITIATED_CONFLICT_END";
}

function userConflictEndEvent(time: Date) {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canUserConflictEnd(getState().games.gameState)) {
            dispatch(userConflictEnd(time));
        } else {
            Sentry.captureMessage("Unexpected userConflictEndEvent");
        }
    };
}

function canUserAbortConflictEnd(gameState: GameState) {
    const status = gameState.status;
    return status === "USER_INITIATED_CONFLICT_END";
}

function userAbortConflictEndEvent() {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canUserAbortConflictEnd(getState().games.gameState)) {
            dispatch(userAbortConflictEnd());
        } else {
            Sentry.captureMessage("Unexpected userAbortConflictEndEvent");
        }
    };
}

function canUserInitiateForceEnd(gameState: GameState) {
    const status = gameState.status;
    return status === "USER_CONFLICT_ENDED";
}

function userInitiateForceEndEvent(transactionHash: string) {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canUserInitiateForceEnd(getState().games.gameState)) {
            dispatch(userInitiateForceEnd(transactionHash));
        } else {
            Sentry.captureMessage("Unexpected userInitiateForceEndEvent");
        }
    };
}

function canUserForceEnd(gameState: GameState) {
    const status = gameState.status;
    return status === "USER_INITIATED_FORCE_END";
}

function userForceEndEvent() {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canUserForceEnd(getState().games.gameState)) {
            dispatch(endedWithReason("END_FORCED_BY_USER"));
        } else {
            Sentry.captureMessage("Unexpected userForceEndEvent");
        }
    };
}

function canUserAbortForceEnd(gameState: GameState) {
    const status = gameState.status;
    return status === "USER_INITIATED_FORCE_END";
}

function userAbortForceEndEvent() {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canUserAbortForceEnd(getState().games.gameState)) {
            dispatch(userAbortForceEnd());
        } else {
            Sentry.captureMessage("Unexpected userAbortForceEndEvent");
        }
    };
}

function canServerConflictEnd(gameState: GameState) {
    const status = gameState.status;
    return (
        status === "CREATING" ||
        status === "USER_CONFLICT_ENDED" ||
        status === "USER_INITIATED_CONFLICT_END" ||
        status === "USER_INITIATED_FORCE_END" ||
        status === "ACTIVE" ||
        status === "PLACED_BET"
    );
}

function serverConflictEndEvent() {
    return (dispatch: Dispatch, getState: GetState) => {
        if (canServerConflictEnd(getState().games.gameState)) {
            dispatch(serverConflictEnd());
        } else {
            Sentry.captureMessage("Unexpected serverConflictEndEvent");
        }
    };
}

export const validChainId = (chainId: number | null | undefined) => {
    return chainId === CHAIN_ID;
};

export function loadContractStateCreatedGame() {
    return async (dispatch: Dispatch, getState: GetState) => {
        const {games} = getState();
        const {gameState} = games;
        const {gameId} = gameState;

        const {isConnected, chainId} = getConnection(wagmiConfig);
        if (!isConnected) {
            throw new Error("You need a web3 enabled browser (Metamask)!");
        }

        if (!validChainId(chainId)) {
            throw new Error(`Invalid network! You need to use ${NETWORK_NAME}!`);
        }

        if (gameId === undefined) {
            throw new Error("Invalid game state!");
        }

        const game = await readContract(wagmiConfig, {
            abi,
            address: CONTRACT_ADDRESS,
            functionName: "gameIdGame",
            args: [BigInt(gameId)],
        });

        const status = game[0];

        if (status === ContractStatus.ENDED && canEndGame(gameState)) {
            const publicClient = getPublicClient(wagmiConfig);
            const reasonEnded = await getReasonEnded(publicClient, CONTRACT_ADDRESS, gameId);
            return dispatch(endGameEvent(ContractReasonEnded[reasonEnded] as ReasonEnded));
        } else if (status === ContractStatus.USER_INITIATED_END && canUserConflictEnd(gameState)) {
            return dispatch(userConflictEndEvent(new Date()));
        } else if (status === ContractStatus.SERVER_INITIATED_END && canServerConflictEnd(gameState)) {
            return dispatch(serverConflictEndEvent());
        } else {
            return;
        }
    };
}

export function loadContractGameState() {
    return async (dispatch: Dispatch, getState: GetState) => {
        const {games} = getState();
        const {gameState} = games;

        const {isConnected, chainId} = getConnection(wagmiConfig);
        if (!isConnected) {
            throw new Error("You need a web3 enabled browser (Metamask)!");
        }

        if (!validChainId(chainId)) {
            throw new Error(`Invalid network! You need to use ${NETWORK_NAME}!`);
        }

        if (gameState.status === "CREATING") {
            // special case as we don't know gameId to read contract state!
            if (!gameState.serverHash) {
                throw new Error("Invalid game state!");
            }

            const publicClient = getPublicClient(wagmiConfig);

            const logCreated = await getLogGameCreated(
                publicClient,
                CONTRACT_ADDRESS,
                gameState.serverHash as `0x${string}`,
            );

            if (logCreated) {
                const {gameId, serverEndHash, userEndHash} = logCreated;

                dispatch(activateGameEvent(gameId, serverEndHash, userEndHash));
                return dispatch(loadContractStateCreatedGame());
            }

            if (gameState.createTransactionHash) {
                const receipt = await getTransactionReceipt(wagmiConfig, {
                    hash: gameState.createTransactionHash as `0x${string}`,
                });

                if (receipt.status === "reverted") {
                    return dispatch(endGameEvent("TRANSACTION_FAILURE"));
                }
            }
        } else if (gameState.status !== "ENDED") {
            return dispatch(loadContractStateCreatedGame());
        }
    };
}

export function loadServerGameState() {
    return async (dispatch: Dispatch, getState: GetState) => {
        if (getState().games.gameState.status === "ENDED") {
            return;
        }

        try {
            const {data} = await axios.get("stateChannel/activeGameState");
            const status = data.status;
            const gameId = data.gameId;
            const userHash = data.userHash;

            const gameState = getState().games.gameState;
            if (gameState.status === "CREATING" && status === "ACTIVE" && gameState.userHash === userHash) {
                dispatch(gameCreated(gameId));
            }
        } catch (error: any) {
            if (error.response?.status !== 404) {
                catchError(error, dispatch);
            }
        }
    };
}

export function loadLocalGameState(chainId: number, address: string) {
    return (dispatch: Dispatch) => {
        if (!isLocalStorageAvailable()) {
            Sentry.captureMessage("No local storage support!");
            console.warn("No local storage support!");
        }

        const storedState = localStorage.getItem(`gameState_${address}_${chainId}`);
        if (storedState !== null) {
            const state = JSON.parse(storedState);
            dispatch(restoreState(state.gameState));
        }
    };
}

export function storeGameState(address: string, gameState: State) {
    if (!isLocalStorageAvailable()) {
        Sentry.captureMessage("No local storage support!");
        console.warn("No local storage support! Can not store game state!");
        return;
    }
    localStorage.setItem(
        `gameState_${address}_${gameState.chainId}`,
        JSON.stringify({version: STORAGE_VERSION, gameState}),
    );
}

export function syncGameState(chainId: number, address: string) {
    return async (dispatch: Dispatch) => {
        try {
            dispatch(loadLocalGameState(chainId, address));
        } catch (error) {
            catchError(error, dispatch);
        }

        try {
            // Loading game state from server isn't necessary
            // But it allows us to get the game, which simplifies to load game state from contract
            await dispatch(loadServerGameState());
        } catch (error) {
            catchError(error, dispatch);
        }

        try {
            await dispatch(loadContractGameState());
        } catch (error) {
            catchError(error, dispatch);
        }
    };
}

// TODO: improve, check contract state???
export function serverActiveGame(gameId: number, serverHash: string, userHash: string) {
    return (dispatch: Dispatch, getState: GetState) => {
        const status = getState().games.gameState.status;
        if (status === "ACTIVE") {
            // already active => do nothing
            return;
        }

        dispatch(activateGameEvent(gameId, serverHash, userHash));
    };
}

export function createGame(stake: number, userSeed: string) {
    return async (dispatch: Dispatch, getState: GetState) => {
        const gameState = getState().games.gameState;
        const status = gameState.status;

        if (!isLocalStorageAvailable()) {
            throw new Error(
                "You browser doesn't support sessionStorage/localStorage! Without playing is not possible!",
            );
        }

        const {isConnected, chainId} = getConnection(wagmiConfig);
        if (!isConnected) {
            throw new Error("You need a web3 enabled browser (Metamask)!");
        }

        if (!validChainId(chainId)) {
            throw new Error(`Invalid network! You need to use ${NETWORK_NAME}!`);
        }

        if (!canCreateGame(gameState)) {
            throw new Error(`Invalid game status: ${status}! Can not create game!`);
        }

        const hashChain = createHashChain(userSeed);

        if (gameState.endTransactionHash !== undefined) {
            console.log("Checking previous game end transaction...", gameState.endTransactionHash);
            const transactionReceipt = await getTransactionReceipt(wagmiConfig, {
                hash: gameState.endTransactionHash as `0x${string}`,
            });
            console.log("Previous game end transaction receipt:", transactionReceipt);
            if (!transactionReceipt) {
                throw new Error("You need to wait until transaction ending game session is mined!");
            }
        }

        console.log("Posting create game request to server...");
        const response = await axios.post("stateChannel/createGame");
        const data = response.data;
        const serverEndHash = data.serverEndHash;
        const previousGameId = BigInt(data.previousGameId);
        const createBefore = BigInt(data.createBefore);
        const signature = data.signature;

        dispatch(createGameEvent(hashChain, serverEndHash, stake));
        console.log("Creating game...");
        const {request} = await simulateContract(wagmiConfig, {
            address: CONTRACT_ADDRESS,
            abi,
            functionName: "createGame",
            args: [
                hashChain[0] as `0x${string}`,
                previousGameId,
                createBefore,
                serverEndHash as `0x${string}`,
                signature as `0x${string}`,
            ],
            value: BigInt(fromGweiToWei(stake)),
            gas: 150000n,
        });
        const transactionHash = await writeContract(wagmiConfig, request);

        dispatch(createGameEvent(hashChain, serverEndHash, stake, transactionHash));

        console.log("Waiting for create game transaction receipt...", transactionHash);
        const receipt = await waitForTransactionReceipt(wagmiConfig, {hash: transactionHash, confirmations: 2});
        console.log("Create game transaction receipt:", receipt);

        if (receipt.status === "reverted") {
            dispatch(endGameEvent("TRANSACTION_FAILURE"));
            throw new Error("Create game transaction failed!");
        }

        const logs = parseEventLogs({
            abi,
            logs: receipt.logs,
        });

        logs.forEach((log) => {
            const eventName = log.eventName;

            if (eventName === "LogGameCreated") {
                const args = log.args;
                const gameId = args.gameId;
                const serverHash = args.serverEndHash;
                const userHash = args.userEndHash;
                if (getState().games.gameState.status !== "ACTIVE") {
                    dispatch(activateGameEvent(Number(gameId), serverHash, userHash));
                }
            }
        });
    };
}

export function endGame() {
    return async (dispatch: Dispatch, getState: GetState) => {
        const state = getState();
        const gameState = state.games.gameState;

        // use previous seeds as new hashes seeds (hash chain)
        const serverHash = gameState.serverHash;
        const userHash = gameState.userHash;

        const gameId = gameState.gameId;
        const roundId = gameState.roundId + 1;
        const balance = gameState.balance;

        if (!state.account.jwt) {
            throw new Error("You need to login before ending game session!");
        }

        const {isConnected, chainId} = getConnection(wagmiConfig);
        if (!isConnected) {
            throw new Error("You need a web3 enabled browser (Metamask)!");
        }

        if (!validChainId(chainId)) {
            throw new Error(`Invalid network! You need to use ${NETWORK_NAME}!`);
        }

        if (!canRegularEndGame(gameState)) {
            throw new Error(`Invalid game status ${gameState.status}! Can not end game!`);
        }

        if (!userHash || !serverHash /* || !userAddress*/ || !gameId) {
            throw new Error("Invalid state!");
        }

        const bet = {
            roundId: gameState.roundId + 1,
            gameType: 0,
            num: 0,
            value: 0,
            balance,
            serverHash,
            userHash,
            gameId,
        };

        const typedData = createTypedData(bet, CHAIN_ID, CONTRACT_ADDRESS, SIGNATURE_VERSION);
        const userSig = await signTypedData(wagmiConfig, typedData);

        const {data} = await axios.post("stateChannel/endGame", {bet, contractAddress: CONTRACT_ADDRESS, userSig});
        const {serverSig, transactionHash: endTransactionHash} = data;

        if (!verifySignature(bet, CHAIN_ID, CONTRACT_ADDRESS, serverSig, SERVER_ADDRESS, SIGNATURE_VERSION)) {
            throw new Error("Invalid server signature!");
        }

        dispatch(regularEndGameEvent(roundId, serverHash, userHash, serverSig, userSig, endTransactionHash));
    };
}

export function userEndGame() {
    return async (dispatch: Dispatch, getState: GetState) => {
        const state = getState();
        const gameState = state.games.gameState;

        const {isConnected, chainId} = getConnection(wagmiConfig);
        if (!isConnected) {
            throw new Error("You need a web3 enabled browser (Metamask)!");
        }

        if (!validChainId(chainId)) {
            throw new Error(`Invalid network! You need to use ${NETWORK_NAME}!`);
        }

        if (!canUserEndGame(gameState)) {
            throw new Error(
                `Invalid game status ${gameState.status}, ${gameState.reasonEnded}! Can not resend end transaction!`,
            );
        }

        const roundId = gameState.roundId;
        const balance = gameState.balance;
        const serverHash = gameState.serverHash!;
        const userHash = gameState.userHash!;
        const gameId = gameState.gameId!;
        const serverSig = gameState.serverSig!;

        const {request} = await simulateContract(wagmiConfig, {
            address: CONTRACT_ADDRESS,
            abi,
            functionName: "userEndGame",
            args: [
                roundId,
                BigInt(fromGweiToWei(balance)),
                serverHash as `0x${string}`,
                userHash as `0x${string}`,
                BigInt(gameId),
                CONTRACT_ADDRESS,
                serverSig as `0x${string}`,
            ],
            gas: BigInt(120000),
        });
        const transactionHash = await writeContract(wagmiConfig, request);

        const receipt = await waitForTransactionReceipt(wagmiConfig, {hash: transactionHash, confirmations: 1});
        if (receipt.status === "success") {
            // nothing to do
            //TODO: Store transaction hash???
        } else {
            throw new Error("Transaction failed!");
        }
    };
}

export function conflictEnd() {
    return async (dispatch: Dispatch, getState: GetState) => {
        const state = getState();
        const gameState = state.games.gameState;

        const gameId = gameState.gameId;
        const roundId = gameState.roundId;
        const gameType = gameState.gameType;
        const num = gameState.num;
        const oldBalance = gameState.oldBalance;
        const serverSig = gameState.serverSig;

        const {isConnected, chainId} = getConnection(wagmiConfig);
        if (!isConnected) {
            throw new Error("You need a web3 enabled browser (Metamask)!");
        }

        if (!validChainId(chainId)) {
            throw new Error(`Invalid network! You need to use ${NETWORK_NAME}!`);
        }

        if (!canUserInitiateConflictEnd(gameState)) {
            throw new Error(`Invalid game status ${gameState.status}! Can not conflict end!`);
        }

        if (!gameState.serverHash || !gameState.userHash || gameId === undefined) {
            throw new Error("Invalid state!");
        }

        if (roundId === 0) {
            const {request} = await simulateContract(wagmiConfig, {
                address: CONTRACT_ADDRESS,
                abi,
                functionName: "userCancelActiveGame",
                args: [BigInt(gameId)],
                gas: BigInt(120000),
            });
            const transactionHash = await writeContract(wagmiConfig, request);

            dispatch(userInitiateConflictEndEvent(transactionHash));
            const receipt = await waitForTransactionReceipt(wagmiConfig, {hash: transactionHash, confirmations: 1});
            if (receipt.status === "success") {
                dispatch(userConflictEndEvent(new Date()));
            } else {
                dispatch(userAbortConflictEndEvent());
            }
        } else {
            let serverHash = keccak(gameState.serverHash);
            let userHash = keccak(gameState.userHash);
            const value = fromGweiToWei(gameState.betValue!).toString();
            let balance = fromGweiToWei(oldBalance).toString();
            let userSeed = gameState.userHash;

            if (gameState.status === "PLACED_BET") {
                serverHash = gameState.serverHash as `0x${string}`;
                userHash = gameState.userHash as `0x${string}`;
                balance = fromGweiToWei(gameState.balance).toString();
                userSeed = gameState.hashChain[roundId];
            }

            const {request} = await simulateContract(wagmiConfig, {
                address: CONTRACT_ADDRESS,
                abi,
                functionName: "userEndGameConflict",
                args: [
                    roundId,
                    gameType,
                    BigInt(num),
                    BigInt(value),
                    BigInt(balance),
                    serverHash,
                    userHash,
                    BigInt(gameId),
                    serverSig as `0x${string}`,
                    userSeed as `0x${string}`,
                ],
                gas: BigInt(250000),
            });
            const transactionHash = await writeContract(wagmiConfig, request);

            dispatch(userInitiateConflictEndEvent(transactionHash));

            const receipt = await waitForTransactionReceipt(wagmiConfig, {hash: transactionHash, confirmations: 1});
            if (receipt.status === "success") {
                dispatch(userConflictEndEvent(new Date()));
            } else {
                dispatch(userAbortConflictEndEvent());
            }
        }
    };
}

export function forceEnd() {
    return async (dispatch: Dispatch, getState: GetState) => {
        const state = getState();
        const gameState = state.games.gameState;

        const gameId = gameState.gameId;

        const {isConnected, chainId} = getConnection(wagmiConfig);
        if (!isConnected) {
            throw new Error("You need a web3 enabled browser (Metamask)!");
        }

        if (!validChainId(chainId)) {
            throw new Error(`Invalid network! You need to use ${NETWORK_NAME}!`);
        }

        if (!canUserInitiateForceEnd(gameState)) {
            throw new Error(`Invalid game status ${gameState.status}! Can not force end!`);
        }

        if (!gameId) {
            throw new Error("Invalid game ID!");
        }

        const {request} = await simulateContract(wagmiConfig, {
            address: CONTRACT_ADDRESS,
            abi,
            functionName: "userForceGameEnd",
            args: [BigInt(gameId)],
            gas: BigInt(120000),
        });
        const transactionHash = await writeContract(wagmiConfig, request);

        dispatch(userInitiateForceEndEvent(transactionHash));

        const transaction = await waitForTransactionReceipt(wagmiConfig, {hash: transactionHash, confirmations: 1});
        if (transaction.status === "success") {
            dispatch(userForceEndEvent());
        } else {
            dispatch(userAbortForceEndEvent());
        }
    };
}

async function revealSeedRequest(gameId: number, roundId: number, userSeed: string) {
    return retry(
        () => {
            return axios.post("stateChannel/revealSeed", {
                gameId,
                roundId,
                userSeed,
            });
        },
        {retries: 1, minTimeout: 500},
    );
}

export function requestSeed() {
    return async (dispatch: Dispatch, getState: GetState) => {
        const gameState = getState().games.gameState;
        const serverHash = gameState.serverHash;

        if (!getState().account.jwt) {
            throw new Error("You need to login before playing!");
        }

        if (!canRevealSeed(gameState)) {
            throw new Error(`Invalid game status: ${gameState.status}! Can not reveal seed!`);
        }

        const betValue = gameState.betValue;
        const userSeed = gameState.hashChain[gameState.roundId];
        const num = gameState.num;
        const gameType = gameState.gameType;
        const balance = gameState.balance;

        if (betValue === undefined || gameState.gameId === undefined || !serverHash) {
            throw new Error("Invalid game state!");
        }

        const {data} = await revealSeedRequest(gameState.gameId, gameState.roundId, userSeed);
        const {serverSeed, balance: newServerBalance, bet} = data;

        if (!verifySeed(serverSeed, serverHash)) {
            throw new Error("Invalid server seed!");
        }

        const resNum = calcResultNumber(gameType, serverSeed, userSeed, num);
        const userProfit = calcUserProfit(gameType, num, betValue, resNum);
        const newUserBalance = balance + userProfit;

        if (newServerBalance !== newUserBalance) {
            throw new Error(`Invalid server balance! Expected ${newUserBalance} got ${newServerBalance}`);
        }

        dispatch(revealSeedEvent(serverSeed, userSeed, newServerBalance));

        return {
            betNum: bet.num,
            num: resNum,
            won: userProfit > 0,
            userProfit,
            bet,
        };
    };
}

export function manualRequestSeed() {
    return async (dispatch: Dispatch) => {
        const result = await dispatch(requestSeed());
        dispatch(addNewBet(result.bet));
        return result;
    };
}

export function placeBet(num: number, betValue: number, gameType: number) {
    return async (
        dispatch: Dispatch,
        getState: GetState,
    ): Promise<{betNum: number; num: number; won: boolean; userProfit: number; bet: FinalBet}> => {
        const gameState = getState().games.gameState;

        // use previous seeds as new hashes seeds (hash chain)
        const serverHash = gameState.serverHash;
        const userHash = gameState.userHash;

        const roundId = gameState.roundId + 1;
        const gameId = gameState.gameId;
        const balance = gameState.balance;
        const stake = gameState.stake;

        if (!getState().account.jwt) {
            throw new Error("You need to login before playing!");
        }

        const {isConnected, chainId} = getConnection(wagmiConfig);
        if (!isConnected) {
            throw new Error("You need a web3 enabled browser (Metamask)!");
        }

        if (!validChainId(chainId)) {
            throw new Error(`Invalid network! You need to use ${NETWORK_NAME}!`);
        }

        if (!canPlaceBet(gameState)) {
            throw new Error(`Invalid game status: ${gameState.status}! Can not place bet!`);
        }

        if (!serverHash || !userHash || gameId === undefined) {
            throw new Error("Invalid game state!");
        }

        if (betValue > stake + balance) {
            throw new Error("Invalid bet value: Funds to low!");
        }

        const bet = {
            roundId,
            gameType,
            num,
            value: betValue,
            balance,
            serverHash,
            userHash,
            gameId,
        };

        const typedData = createTypedData(bet, CHAIN_ID, CONTRACT_ADDRESS, SIGNATURE_VERSION);
        const userSig = await signTypedData(wagmiConfig, typedData);

        const {data: dataPlaceBet} = await axios.post("stateChannel/placeBet", {
            bet,
            contractAddress: CONTRACT_ADDRESS,
            userSig,
        });
        const serverSig = dataPlaceBet.serverSig;

        if (!verifySignature(bet, CHAIN_ID, CONTRACT_ADDRESS, serverSig, SERVER_ADDRESS, SIGNATURE_VERSION)) {
            throw new Error("Error placing bet: Invalid server signature!");
        }

        dispatch(placeBetEvent(bet, serverSig, userSig));

        return dispatch(requestSeed());
    };
}
